export function getOrderCategoryIds(guests = []) {
  return [...new Set(guests.map((g) => g.category?.id).filter((id) => id != null))]
}

export function getOrderCategoryTitles(guests = []) {
  return [
    ...new Set(
      guests
        .map((g) => {
          const cat = g.category
          if (cat && typeof cat === 'object') return cat.title || ''
          return ''
        })
        .filter(Boolean)
    ),
  ]
}

export function isRoomTypeCategoryEligible(type, orderCategoryIds) {
  if (!orderCategoryIds.length) return true
  const roomCategoryIds = (type.categories || []).map((c) => c.id)
  return orderCategoryIds.every((id) => roomCategoryIds.includes(id))
}

export function getRoomTypeCategoryTitles(type) {
  return (type.categories || []).map((c) => c.title).filter(Boolean)
}

export function normalizeBookingDate(date) {
  if (!date) return ''
  const str = String(date).trim()
  if (!str) return ''
  const normalized = str.slice(0, 10).replace(/\//g, '-')
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : ''
}

export function getEarliestBookingDate(bookings = []) {
  if (!Array.isArray(bookings) || !bookings.length) return ''
  const dates = bookings.map((b) => normalizeBookingDate(b?.date)).filter(Boolean).sort()
  return dates[0] || ''
}

export function roomIdsMatch(a, b) {
  if (a == null || b == null) return false
  return String(a) === String(b)
}

export function datesMatch(a, b) {
  return normalizeBookingDate(a) === normalizeBookingDate(b)
}

function normalizeRoomTitle(title) {
  return String(title || '')
    .trim()
    .replace(/\s+/g, '')
}

export function roomTitlesMatch(a, b) {
  const left = normalizeRoomTitle(a)
  const right = normalizeRoomTitle(b)
  return left && right && left === right
}

export function resolveWeekRange(dateStr) {
  const normalized = normalizeBookingDate(dateStr)
  if (!normalized) return { start: '', end: '' }

  const date = new Date(normalized.replace(/-/g, '/'))
  if (Number.isNaN(date.getTime())) return { start: '', end: '' }

  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(date)
  monday.setDate(diff)

  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)

  const format = (d) => {
    const y = d.getFullYear()
    const m = d.getMonth() + 1
    const dayNum = d.getDate()
    return `${y}-${String(m).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`
  }

  return { start: format(monday), end: format(sunday) }
}

export function findRoomForBooking(type, booking) {
  const rooms = type.rooms || []
  const bookingRoomId = booking.roomId

  if (bookingRoomId != null) {
    const byId = rooms.find((r) => roomIdsMatch(r.roomId, bookingRoomId))
    if (byId) return byId
  }

  const bookingTitle = booking.title || booking.roomName
  if (bookingTitle) {
    return rooms.find((r) => roomTitlesMatch(r.roomTitle, bookingTitle)) || null
  }

  return null
}

function resolveSelectionEntry(type, room, booking) {
  const date = normalizeBookingDate(booking.date)
  if (!date) return null

  const dateRoom = (room.availabilityByDate || []).find((d) => datesMatch(d.date, date))
  if (!dateRoom) return null

  return {
    roomId: room.roomId,
    date: normalizeBookingDate(dateRoom.date) || date,
    room,
    type,
    dateRoom,
  }
}

export function mapInitialBookingsToSelected(roomTypeList, initialBookings) {
  if (!Array.isArray(roomTypeList) || !Array.isArray(initialBookings)) return []

  const selected = []

  initialBookings.forEach((booking) => {
    const date = normalizeBookingDate(booking.date)
    if (!date) return

    for (const type of roomTypeList) {
      const room = findRoomForBooking(type, booking)
      if (!room) continue

      const entry = resolveSelectionEntry(type, room, booking)
      if (!entry) continue

      selected.push(entry)
      break
    }
  })

  return selected.sort((a, b) => String(a.date).localeCompare(String(b.date)))
}

function selectionKey(item) {
  return `${String(item.roomId)}|${normalizeBookingDate(item.date)}`
}

export function mergeBookingsIntoSelection(selectedRoomsList, roomTypeList, initialBookings) {
  const existing = Array.isArray(selectedRoomsList) ? [...selectedRoomsList] : []
  const keySet = new Set(existing.map(selectionKey))
  const mapped = mapInitialBookingsToSelected(roomTypeList, initialBookings)

  mapped.forEach((entry) => {
    const key = selectionKey(entry)
    if (keySet.has(key)) return
    keySet.add(key)
    existing.push(entry)
  })

  return existing.sort((a, b) => String(a.date).localeCompare(String(b.date)))
}

export function bookingMatchesRoomDate(booking, room, date) {
  const bookingDate = normalizeBookingDate(booking?.date)
  const cellDate = normalizeBookingDate(date)
  if (!bookingDate || !cellDate || bookingDate !== cellDate) return false

  if (booking.roomId != null && roomIdsMatch(booking.roomId, room?.roomId)) return true

  const bookingTitle = booking.title || booking.roomName
  if (bookingTitle && roomTitlesMatch(bookingTitle, room?.roomTitle)) return true

  return false
}

export function checkDateContinuity(selectedRoomsList) {
  if (!selectedRoomsList || selectedRoomsList.length <= 1) return true

  const sortedTimestamps = selectedRoomsList
    .map((item) => new Date(String(item.date).replace(/-/g, '/')).getTime())
    .sort((a, b) => a - b)

  const ONE_DAY_MS = 24 * 60 * 60 * 1000

  for (let i = 0; i < sortedTimestamps.length - 1; i++) {
    if (Math.round(sortedTimestamps[i + 1] - sortedTimestamps[i]) !== ONE_DAY_MS) {
      return false
    }
  }
  return true
}

function toBookingSelectionKey(booking) {
  const date = normalizeBookingDate(booking?.date)
  if (!date || booking?.roomId == null) return ''
  return `${date}|${String(booking.roomId)}`
}

/** 比對原 bookings 與新選取是否為同一組 (date, roomId)，順序無關 */
export function isSameBookingSelection(originalBookings = [], selectedRooms = []) {
  if (!Array.isArray(originalBookings) || !Array.isArray(selectedRooms)) return false
  if (originalBookings.length !== selectedRooms.length) return false
  if (!originalBookings.length) return true

  const originalKeys = originalBookings.map(toBookingSelectionKey).filter(Boolean).sort()
  const selectedKeys = selectedRooms.map(toBookingSelectionKey).filter(Boolean).sort()
  if (originalKeys.length !== selectedKeys.length || originalKeys.length !== originalBookings.length) {
    return false
  }
  return originalKeys.every((key, index) => key === selectedKeys[index])
}
